import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useState } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import PortalIdParser from 'PortalIdParser';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import I18n from 'I18n';
import { Small } from 'foundations-components/transitional/components/Small';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { EnrollTypes, EnrollmentStatuses } from '../../constants';
import formatName from 'I18n/utils/formatName';
import { selectAuthenticatedUser } from '../../../app/auth/AuthSelectors';
import { ENROLL_BETA, GET_ROLLOUT_PRODUCT_UPDATES, GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID, GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID, updateProductUpdateCache } from '../../queries';
import { useMutation } from 'data-fetching-client';
import { addErrorAlert, addSuccessAlert } from '../../../app/utils/alerting';
import { TrackingAction, TrackingFeature, TrackingScreen, TrackingSubscreen, trackBetaEnrollmentChange, trackInteractionWithId } from '../../../app/utils/tracking';
import { useAppSelector } from '../../../app/indexReducer';
import OneOfReasonUITooltip from '../utils/OneOfReasonUITooltip';
import { getRequestParamsForGetRolloutProductUpdates } from '../../utils';
import useFetchOrgData from '../../hooks/useFetchOrgData';
import { getIsOptionDisabled } from '../utils/enrollmentStatus';
import { Link } from 'foundations-components/transitional/components/Link';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const JoinPublicBetaDialog = ({
  productUpdateId,
  rolloutId,
  enrollType,
  title,
  untranslatedTitle,
  gate,
  isSlowRollout = false,
  onClose
}) => {
  const portalId = PortalIdParser.get();
  const user = useAppSelector(selectAuthenticatedUser);
  const [isTermsAcknowledged, setIsTermsAcknowledged] = useState(false);
  const {
    isInOrg,
    portalsInOrgCount
  } = useFetchOrgData();
  const isOrgOnly = enrollType === 'ORGANIZATIONS_ONLY';
  const defaultState = enrollType === 'USERS_ONLY' ? 'USER' : isOrgOnly ? 'ORGANIZATION' : 'PORTAL';
  const [desiredState, setDesiredState] = useState(defaultState);
  const isPortalOptionDisabled = getIsOptionDisabled('PORTAL', enrollType);
  const isUserOptionDisabled = getIsOptionDisabled('USER', enrollType);
  const isOrgOptionDisabled = getIsOptionDisabled('ORGANIZATION', enrollType);
  const [joinBeta, {
    loading,
    error
  }] = useMutation(ENROLL_BETA, {
    refetchQueries: [{
      query: GET_ROLLOUT_PRODUCT_UPDATES,
      variables: getRequestParamsForGetRolloutProductUpdates({
        app: null,
        calendarTimestamp: null,
        endDate: null,
        impactLevels: null,
        isFavorite: null,
        hasAiFeature: null,
        limit: null,
        offset: null,
        product: null,
        puQuery: null,
        recommended: null,
        rolloutState: null,
        startDate: null,
        type: null,
        view: null,
        category: 'your-betas',
        betaStatus: 'enrolled'
      })
    }, {
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID,
      variables: {
        id: rolloutId
      }
    }, {
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID,
      variables: {
        id: productUpdateId
      }
    }],
    update: (cache, res) => {
      var _res$data;
      const approvedAt = (_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.enrollBeta.approvedAt;
      updateProductUpdateCache(cache, {
        productUpdateId: `${productUpdateId}`,
        rolloutId: `${rolloutId}`,
        nextEnrollmentStatus: desiredState,
        approvedAt
      });
    }
  });
  const userId = user && user.user_id;
  const handleJoin = useCallback(() => {
    if (!portalId || !userId) {
      addErrorAlert(desiredState === EnrollmentStatuses.USER ? 'app.betas.errors.problemJoiningYou' : desiredState === EnrollmentStatuses.PORTAL ? 'app.betas.errors.problemJoiningAccount' : 'app.betas.errors.problemJoiningOrganization');
      return;
    }
    joinBeta({
      variables: {
        rolloutId,
        portalId,
        enrollId: desiredState === EnrollmentStatuses.USER ? userId : portalId,
        enrollType: desiredState === EnrollmentStatuses.USER ? EnrollTypes.USERS_ONLY : desiredState === EnrollmentStatuses.PORTAL ? EnrollTypes.PORTALS_ONLY : EnrollTypes.ORGANIZATIONS_ONLY,
        betaAccessType: 'PUBLIC'
      }
    }).then(() => {
      trackBetaEnrollmentChange({
        title,
        untranslatedTitle,
        gateName: gate,
        currentState: EnrollmentStatuses.NOT_ENROLLED,
        desiredState
      });
      addSuccessAlert(desiredState === EnrollmentStatuses.USER ? 'app.betas.success.youJoined' : desiredState === EnrollmentStatuses.PORTAL ? 'app.betas.success.accountJoined' : 'app.betas.success.organizationJoined', title);
    }).catch(() => {
      addErrorAlert(desiredState === EnrollmentStatuses.USER ? 'app.betas.errors.problemJoiningYou' : desiredState === EnrollmentStatuses.PORTAL ? 'app.betas.errors.problemJoiningAccount' : 'app.betas.errors.problemJoiningOrganization');
    }).finally(onClose);
  }, [portalId, userId, rolloutId, desiredState, gate, joinBeta, onClose, title, untranslatedTitle]);
  if (!user) {
    return /*#__PURE__*/_jsx(_Fragment, {});
  }
  return /*#__PURE__*/_jsxs(Modal, {
    use: "success",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.join.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(Paragraph, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "app.betas.join.content",
          options: {
            betaName: title
          }
        })
      }), /*#__PURE__*/_jsxs(ToggleGroup, {
        name: "beta-target",
        marginBlockStart: "600",
        children: [/*#__PURE__*/_jsx(OneOfReasonUITooltip, {
          placement: "bottom",
          oneOf: [{
            showWhen: isUserOptionDisabled,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.disabledOption"
            })
          }, {
            showWhen: isSlowRollout,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.disabledOptionSlowRollout"
            })
          }],
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-user-radio",
            checked: desiredState === EnrollmentStatuses.USER,
            disabled: isUserOptionDisabled || isSlowRollout,
            onChange: () => setDesiredState('USER'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.adminOption",
              options: {
                name: formatName({
                  givenName: user.first_name,
                  familyName: user.last_name,
                  email: user.email
                })
              }
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isPortalOptionDisabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.join.disabledOption"
          }),
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-portal-radio",
            checked: desiredState === EnrollmentStatuses.PORTAL,
            disabled: isPortalOptionDisabled,
            onChange: () => setDesiredState('PORTAL'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.accountOption",
              options: {
                portalId: I18n.SafeString(portalId.toString())
              }
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isOrgOptionDisabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.join.disabledOption"
          }),
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-organization-radio",
            checked: desiredState === EnrollmentStatuses.ORGANIZATION,
            disabled: isOrgOptionDisabled,
            onChange: () => setDesiredState('ORGANIZATION'),
            children: isOrgOnly && isInOrg ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.organizationOption",
              options: {
                portalsInOrg: I18n.SafeString(portalsInOrgCount.toString())
              }
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.organizationOptionDefault"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(FormControl, {
        marginBlock: "500",
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: isTermsAcknowledged,
          onChange: ({
            target: {
              checked
            }
          }) => {
            trackInteractionWithId(TrackingScreen.PRODUCT_UPDATES, TrackingSubscreen.PUBLIC_BETAS, TrackingFeature.BETA_LEGAL_TERMS, TrackingAction.CLICK, `product update ID ${productUpdateId}`);
            setIsTermsAcknowledged(checked);
          },
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "app.betas.terms.description_jsx",
            options: {
              href: 'https://legal.hubspot.com/hubspot-beta-terms',
              external: true
            },
            elements: {
              Link
            }
          })
        })
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx("b", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.join.note"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "join-beta-modal-accept-btn",
        disabled: !isTermsAcknowledged,
        failed: Boolean(error),
        loading: loading,
        onClick: handleJoin,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.join.action"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.join.cancel"
        })
      })]
    })]
  });
};
export default JoinPublicBetaDialog;